'use client'

import { useMemo, useState } from 'react';
import {
  ModalOverlay,
  ModalContainer,
  CloseButton,
  IconContainer,
  Title,
  Description,
  SelectContainer,
  Select,
  SelectArrow,
  ButtonsContainer,
  SelectButton,
  EscapeButton,
} from './styles';
import { useGlobalQueryParams } from '../../hooks/queryParams/useGlobalQueryParams';
import { useUnitSelection } from '../../hooks/useUnitSelection';
import { getUnitDisplayName } from '../../utils/unit';
import {
  buildUnitGroups,
  DEFAULT_UNIT_GROUPING_LABEL,
  type UnitGroupConfig,
  type UnitGroupingMode,
} from '@autodromo/units';

type Unit = {
  id: string;
  name: string;
  displayName: string;
  state: string | null;
  city: string | null;
};

interface UnitSelectionModalProps {
  enabled: boolean;
  required: boolean;
  title: string;
  description: string;
  color: string;
  units: Unit[];
  unitGroupingMode?: UnitGroupingMode | null;
  unitGroups?: UnitGroupConfig[] | null;
  unitGroupingLabel?: string | null;
}

const MapPinIcon = ({ color }: { color: string }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export const UnitSelectionModal = ({
  enabled,
  required,
  title,
  description,
  color,
  units,
  unitGroupingMode,
  unitGroups,
  unitGroupingLabel,
}: UnitSelectionModalProps) => {
  const [wasClosed, setWasClosed] = useState(false);
  const [draftGroupSlug, setDraftGroupSlug] = useState<string>('');
  const [draftUnitId, setDraftUnitId] = useState<string>('');

  const { hasStoredChoice, selectUnit, selectGroup, dismiss } = useUnitSelection({
    useQueryParams: useGlobalQueryParams,
  });

  const groupingLabel =
    unitGroupingLabel?.trim() || DEFAULT_UNIT_GROUPING_LABEL;

  const groups = useMemo(
    () => buildUnitGroups({ units, mode: unitGroupingMode, groups: unitGroups }),
    [units, unitGroupingMode, unitGroups],
  );

  const handleConfirm = () => {
    if (groups) {
      if (!draftGroupSlug) return;
      selectGroup(draftGroupSlug);
    } else {
      if (!draftUnitId) return;
      selectUnit(draftUnitId);
    }

    setWasClosed(true);
  };

  const handleClose = () => {
    if (required) return;

    dismiss();
    setWasClosed(true);
  };

  if (!enabled || wasClosed || hasStoredChoice !== false) {
    return null;
  }

  const hasSelection = Boolean(groups ? draftGroupSlug : draftUnitId);

  return (
    <ModalOverlay onClick={required ? undefined : handleClose}>
      <ModalContainer
        role="dialog"
        aria-modal="true"
        aria-labelledby="unit-selection-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        {!required && (
          <CloseButton onClick={handleClose} aria-label="Fechar">
            <CloseIcon />
          </CloseButton>
        )}

        <IconContainer bgColor={color}>
          <MapPinIcon color="#FFFFFF" />
        </IconContainer>

        <Title id="unit-selection-modal-title">{title}</Title>

        <Description>{description}</Description>

        {groups ? (
          <SelectContainer>
            <Select
              value={draftGroupSlug}
              aria-label={groupingLabel}
              onChange={(e) => setDraftGroupSlug(e.target.value)}
            >
              <option value="">{groupingLabel}</option>
              {groups.map((group) => (
                <option key={group.slug} value={group.slug}>
                  {group.name}
                </option>
              ))}
            </Select>
            <SelectArrow>
              <ChevronDownIcon />
            </SelectArrow>
          </SelectContainer>
        ) : (
          <SelectContainer>
            <Select
              value={draftUnitId}
              aria-label="Unidade"
              onChange={(e) => setDraftUnitId(e.target.value)}
            >
              <option value="">Selecione a unidade</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {getUnitDisplayName({ unit })}
                </option>
              ))}
            </Select>
            <SelectArrow>
              <ChevronDownIcon />
            </SelectArrow>
          </SelectContainer>
        )}

        <ButtonsContainer hasEscapeButton={!required}>
          {!required && (
            <EscapeButton buttonColor={color} onClick={handleClose}>
              Ver todas as ofertas
            </EscapeButton>
          )}
          <SelectButton
            bgColor={color}
            disabled={!hasSelection}
            onClick={handleConfirm}
          >
            {groups ? 'Selecionar' : 'Selecionar unidade'}
          </SelectButton>
        </ButtonsContainer>
      </ModalContainer>
    </ModalOverlay>
  );
};
